import { Dialog, DialogClose, DialogContent, DialogTrigger } from "@breakout/ui/components/Dialog"
import { ArrowRight, HelpCircle, TrendingUp, Trophy, Users } from "lucide-react"

const STEPS = [
	{
		number: 1,
		title: "Spot the Next Breakout",
		description:
			"Each week features a new set of creators. Find who you think will climb (or fall) in the rankings.",
		icon: Users,
		color: "text-breakout-brand",
		bgColor: "bg-breakout-brand/10",
		ringColor: "ring-breakout-brand/30",
	},
	{
		number: 2,
		title: "Buy Your Position",
		description: (
			<>
				<span className="font-semibold text-breakout-brand">Long</span> = they'll climb.{" "}
				<span className="font-semibold text-breakout-secondary">Short</span> = they'll drop. The
				earlier you buy, the better your price.
			</>
		),
		icon: TrendingUp,
		color: "text-breakout-secondary",
		bgColor: "bg-breakout-secondary/10",
		ringColor: "ring-breakout-secondary/30",
	},
	{
		number: 3,
		title: "Cash Out",
		description:
			"At week's end, markets settle based on final rankings. Correct predictions pay out from the pool.",
		icon: Trophy,
		color: "text-amber-500",
		bgColor: "bg-amber-500/10",
		ringColor: "ring-amber-500/30",
	},
] as const

export function HowItWorksModal({ children }: { children?: React.ReactNode }) {
	return (
		<Dialog>
			<DialogTrigger asChild>
				{children ?? (
					<button
						aria-label="How it works"
						className="flex size-9 items-center justify-center rounded-full text-text-muted transition-all hover:bg-bg-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95"
						type="button"
					>
						<HelpCircle aria-hidden="true" className="size-5" />
					</button>
				)}
			</DialogTrigger>
			<DialogContent className="max-w-md sm:max-w-lg sm:rounded-3xl">
				{/* Decorative top accent */}
				<div className="h-1 w-full bg-breakout-brand" />

				<div className="relative p-5 sm:p-8">
					<div className="relative">
						{/* Header */}
						<div className="mb-6 text-center sm:mb-8">
							<h2 className="font-bold font-display text-text-primary text-xl sm:text-2xl">
								How Breakout Works
							</h2>
							<p className="mt-1.5 text-sm text-text-secondary sm:text-base">
								Predict who's about to pop off — and profit
							</p>
						</div>

						{/* Steps */}
						<div className="relative">
							{/* Vertical connector line */}
							<div className="absolute top-6 bottom-6 left-5 w-px bg-border-subtle sm:left-6" />

							<div className="space-y-4 sm:space-y-5">
								{STEPS.map(step => {
									const Icon = step.icon
									return (
										<div className="relative flex items-start gap-3 sm:gap-4" key={step.number}>
											{/* Step indicator */}
											<div
												className={`relative z-10 flex size-10 shrink-0 items-center justify-center rounded-xl ${step.bgColor} ring-1 ${step.ringColor} sm:size-12 sm:rounded-2xl`}
											>
												<Icon aria-hidden="true" className={`size-5 ${step.color} sm:size-6`} />
											</div>

											{/* Content */}
											<div className="min-w-0 flex-1 pt-0.5 sm:pt-1">
												<h3 className="font-semibold text-sm text-text-primary sm:text-base">
													<span className={`${step.color} mr-1.5`}>{step.number}.</span>
													{step.title}
												</h3>
												<p className="mt-0.5 text-text-secondary text-xs leading-relaxed sm:mt-1 sm:text-sm">
													{step.description}
												</p>
											</div>
										</div>
									)
								})}
							</div>
						</div>

						{/* CTA Button */}
						<div className="mt-6 sm:mt-8">
							<DialogClose asChild>
								<button
									className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-breakout-brand px-4 py-3 font-semibold text-black shadow-glow-sm transition-all hover:bg-breakout-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 active:scale-[0.98] sm:py-3.5"
									type="button"
								>
									Start Trading
									<ArrowRight aria-hidden="true" className="size-4" />
								</button>
							</DialogClose>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
